import type { Key } from 'react';

interface ShiftSelectionInput {
  orderedKeys: readonly Key[];
  selectedKeys: readonly Key[];
  anchorKey: Key | null;
  targetKey: Key;
  isSelectable: (key: Key) => boolean;
}

interface ShiftSelection {
  selectedKeys: Key[];
  anchorKey: Key;
}

interface PageInput<T> {
  rows: readonly T[];
  current: number;
  pageSize: number;
}

/** Rows antd shows on the page; a page past the end shows the last one. */
export const getPageRows = <T,>({ rows, current, pageSize }: PageInput<T>): T[] => {
  const page = Math.min(current, Math.max(1, Math.ceil(rows.length / pageSize)));
  return rows.slice((page - 1) * pageSize, page * pageSize);
};

/** Shift+Click range: the clicked row's state decides whether the anchor→target range is selected or cleared. */
export const getShiftSelection = ({ orderedKeys, selectedKeys, anchorKey, targetKey, isSelectable }: ShiftSelectionInput): ShiftSelection => {
  const indexOf = (key: Key | null): number => (key === null ? -1 : orderedKeys.findIndex((k) => String(k) === String(key)));
  const selected = new Set(selectedKeys.map(String));
  const targetSelected = selected.has(String(targetKey));
  const anchorIndex = indexOf(anchorKey);
  const targetIndex = indexOf(targetKey);
  if (anchorIndex === -1 || targetIndex === -1) {
    const toggled = targetSelected ? selectedKeys.filter((key) => String(key) !== String(targetKey)) : [...selectedKeys, targetKey];
    return { selectedKeys: toggled, anchorKey: targetKey };
  }
  const range = orderedKeys
    .slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1)
    .filter(isSelectable);
  if (targetSelected) {
    const rangeSet = new Set(range.map(String));
    return { selectedKeys: selectedKeys.filter((key) => !rangeSet.has(String(key))), anchorKey };
  }
  return { selectedKeys: [...selectedKeys, ...range.filter((key) => !selected.has(String(key)))], anchorKey };
};
